// Bundled presets and randomizer recipes. All artwork and names are original
// to Ventholes. Presets are plain documents built on the defaults; recipes
// are seeded, constrained randomizations per style, fully reproducible from
// their seed.

import type { DocumentModel } from './core/types'
import { defaultDocument } from './core/types'
import { makeRng, type Rng } from './core/rng'

export interface Preset {
  name: string
  hint: string
  build: () => DocumentModel
}

function base(mutate: (d: DocumentModel) => void): DocumentModel {
  const d = defaultDocument()
  // Presets were tuned (and are mechanically floor-tested) with whole holes;
  // they pin the drop behavior and opt into other edge modes explicitly.
  d.clip = 'dropTouching'
  d.clipMargin = 0
  mutate(d)
  return d
}

export const PRESETS: Preset[] = [
  {
    name: 'Tide Rings',
    hint: 'Concentric falloff from a quiet center',
    build: () =>
      base((d) => {
        d.layout.mode = 'concentric'
        d.channels.size.base = 2
        d.channels.spacing.base = 6
        d.channels.size.controllers.push(ctrl({ a: { x: 60, y: 40 }, value: 5, radius: 34 }))
      }),
  },
  {
    name: 'Comb Loom',
    hint: 'Tight hex comb with a soft diagonal swell',
    build: () =>
      base((d) => {
        d.channels.spacing.base = 4.4
        d.channels.size.base = 3.1
        d.channels.size.controllers.push(
          ctrl({ kind: 'line', a: { x: 0, y: 0 }, b: { x: 120, y: 80 }, value: 1.2, radius: 30 }),
        )
      }),
  },
  {
    name: 'Auric Bloom',
    hint: 'Phyllotaxis stars opening toward the rim',
    build: () =>
      base((d) => {
        d.layout.mode = 'fibonacci'
        d.boundary.kind = 'circle'
        d.boundary.radius = 38
        d.shape.kind = 'star'
        d.channels.spacing.base = 6.5
        d.channels.size.base = 2
        d.channels.size.controllers.push(ctrl({ a: { x: 60, y: 40 }, value: 4.6, radius: 40, falloff: 'soft' }))
      }),
  },
  {
    name: 'Static Veil',
    hint: 'Fine scatter thinning to one side',
    build: () =>
      base((d) => {
        d.layout.mode = 'scatter'
        d.channels.size.base = 1.4
        d.channels.spacing.base = 4
        d.channels.spacing.controllers.push(
          ctrl({ kind: 'line', a: { x: 120, y: 0 }, b: { x: 120, y: 80 }, value: 9, radius: 60, falloff: 'soft' }),
        )
      }),
  },
  {
    name: 'Basalt Web',
    hint: 'Rounded stone cracks',
    build: () =>
      base((d) => {
        d.layout.mode = 'voronoi'
        d.layout.voronoi.cornerRadius = 1.2
        d.channels.spacing.base = 9
        d.channels.size.base = 2.2
      }),
  },
  {
    name: 'Meridian Sweep',
    hint: 'Flow lines bending around a pole',
    build: () =>
      base((d) => {
        d.layout.mode = 'flowlines'
        d.channels.spacing.base = 5.5
        d.channels.size.base = 2.2
        d.channels.angle.controllers.push(ctrl({ a: { x: 72, y: 34 }, value: 85, radius: 42 }))
      }),
  },
  {
    name: 'Quill Fan',
    hint: 'Crosshatch slots on a shallow fan',
    build: () =>
      base((d) => {
        d.layout.mode = 'crosshatch'
        d.layout.crosshatch.angleA = 18
        d.layout.crosshatch.angleB = -64
        d.shape.kind = 'slot'
        d.channels.size.base = 3.4
        d.channels.spacing.base = 8
        d.channels.angle.base = 18
      }),
  },
  {
    name: 'Ember Drift',
    hint: 'Loose sparks pulled toward a hot spot',
    build: () =>
      base((d) => {
        d.layout.mode = 'scatter'
        d.layout.jitter = 0.6
        d.channels.size.base = 1.8
        d.channels.spacing.base = 6
        d.channels.size.controllers.push(ctrl({ a: { x: 88, y: 52 }, value: 4.4, radius: 26, falloff: 'sharp' }))
      }),
  },
  {
    name: 'Orbit Lattice',
    hint: 'Ring-locked slots riding their tangents',
    build: () =>
      base((d) => {
        d.layout.mode = 'concentric'
        d.shape.kind = 'slot'
        d.shape.slotRatio = 0.3
        d.layout.rotateToPath = true
        d.channels.size.base = 4
        d.channels.spacing.base = 7.5
      }),
  },
  {
    name: 'Crystal Rain',
    hint: 'Slot grid twisting row by row',
    build: () =>
      base((d) => {
        d.shape.kind = 'slot'
        d.layout.grid.pattern = 'rect'
        d.layout.grid.crossRowSpin = 14
        d.channels.size.base = 4
        d.channels.spacing.base = 7
      }),
  },
  {
    name: 'Dune Path',
    hint: 'Beads walking a drawn dune line',
    build: () =>
      base((d) => {
        d.layout.mode = 'path'
        d.clip = 'clip'
        d.layout.paths = [
          {
            id: 'p1',
            kind: 'curve',
            nodes: [
              { x: 8, y: 62 },
              { x: 40, y: 24 },
              { x: 78, y: 58 },
              { x: 112, y: 22 },
            ],
          },
        ]
        d.channels.spacing.base = 7
        d.channels.size.base = 4.2
        d.channels.size.controllers.push(ctrl({ a: { x: 60, y: 40 }, value: 2, radius: 28, falloff: 'soft' }))
      }),
  },
  {
    name: 'Morph Tide',
    hint: 'Circles becoming stars across the sheet',
    build: () =>
      base((d) => {
        d.shapeB.kind = 'star'
        d.channels.size.base = 4.4
        d.channels.spacing.base = 8
        d.channels.shape.controllers.push(
          ctrl({ kind: 'line', a: { x: 120, y: 0 }, b: { x: 120, y: 80 }, value: 1, radius: 70, falloff: 'smooth' }),
        )
      }),
  },
  {
    name: 'Hex Vault',
    hint: 'Rounded panel, margin-safe hex comb',
    build: () =>
      base((d) => {
        d.boundary.cornerRadius = 10
        d.clipMargin = 3
        d.channels.spacing.base = 5.2
        d.channels.size.base = 3.4
        d.channels.size.controllers.push(ctrl({ a: { x: 60, y: 40 }, value: 1.6, radius: 26, falloff: 'soft' }))
      }),
  },
  {
    name: 'Speaker Halo',
    hint: 'Round grille, dense heart, calm rim',
    build: () =>
      base((d) => {
        d.layout.mode = 'concentric'
        d.boundary.kind = 'circle'
        d.boundary.radius = 38
        d.clipMargin = 2.5
        d.channels.spacing.base = 5.4
        d.channels.size.base = 3.2
        d.channels.size.controllers.push(ctrl({ a: { x: 60, y: 40 }, value: 1.4, radius: 42, falloff: 'soft' }))
      }),
  },
  {
    name: 'Turbine Inlet',
    hint: 'Spiral slots riding the swirl',
    build: () =>
      base((d) => {
        d.layout.mode = 'spiral'
        d.boundary.kind = 'circle'
        d.boundary.radius = 38
        d.clipMargin = 2
        d.shape.kind = 'slot'
        d.shape.slotRatio = 0.32
        d.layout.rotateToPath = true
        d.channels.size.base = 4.6
        d.channels.spacing.base = 7
      }),
  },
  {
    name: 'Comb Shear',
    hint: 'A hex field cut on the bias',
    build: () =>
      base((d) => {
        d.layout.grid.angle = 30
        d.boundary.cornerRadius = 6
        d.clipMargin = 2
        d.shape.kind = 'slot'
        d.shape.slotRatio = 0.4
        d.channels.size.base = 4.4
        d.channels.spacing.base = 7.2
      }),
  },
  {
    name: 'Mesa Web',
    hint: 'Broad stone plates, soft shoulders',
    build: () =>
      base((d) => {
        d.layout.mode = 'voronoi'
        d.layout.voronoi.cornerRadius = 2.4
        d.channels.spacing.base = 14
        d.channels.size.base = 3.8
      }),
  },
  {
    name: 'Aurora Veil',
    hint: 'Flat-capped flow around a bright pole',
    build: () =>
      base((d) => {
        d.layout.mode = 'flowlines'
        d.layout.flow.cap = 'flat'
        d.channels.spacing.base = 6.2
        d.channels.size.base = 2.6
        d.channels.angle.controllers.push(ctrl({ a: { x: 40, y: 52 }, value: 65, radius: 40 }))
        d.channels.angle.base = -12
      }),
  },
  {
    name: 'Petal Gate',
    hint: 'Pentagon phyllotaxis in a rounded frame',
    build: () =>
      base((d) => {
        d.layout.mode = 'fibonacci'
        d.boundary.cornerRadius = 14
        d.clipMargin = 3
        d.shape.kind = 'polygon'
        d.shape.sides = 5
        d.channels.spacing.base = 7.5
        d.channels.size.base = 3.6
        d.channels.size.controllers.push(ctrl({ a: { x: 60, y: 40 }, value: 5.4, radius: 44, falloff: 'soft' }))
      }),
  },
  {
    name: 'Star Rain',
    hint: 'Jittered grid dissolving into stars',
    build: () =>
      base((d) => {
        d.layout.jitter = 0.7
        d.clipMargin = 2
        d.shapeB.kind = 'star'
        d.channels.size.base = 3.8
        d.channels.spacing.base = 7.8
        d.channels.shape.controllers.push(
          ctrl({ kind: 'line', a: { x: 0, y: 80 }, b: { x: 120, y: 80 }, value: 1, radius: 55, falloff: 'sharp' }),
        )
      }),
  },
]

function ctrl(over: Record<string, unknown>) {
  return {
    id: 'c' + Math.random().toString(36).slice(2, 10),
    kind: 'point' as const,
    a: { x: 0, y: 0 },
    b: { x: 0, y: 0 },
    nodes: [],
    value: 1,
    radius: 25,
    falloff: 'smooth' as const,
    hardEdge: false,
    enabled: true,
    singleSide: 'off' as const,
    infinite: false,
    syncGroup: null,
    ...over,
  }
}

// Size presets set the canvas and boundary only; the current pattern design
// is kept. Dimensions follow common physical formats; entries marked approx
// should be verified against the actual part.
export interface SizePreset {
  name: string
  hint: string
  canvas: { width: number; height: number }
  boundary: {
    kind: 'rect' | 'circle'
    width?: number
    height?: number
    radius?: number
    cornerRadius?: number
  }
}

export interface SizeGroup {
  group: string
  items: SizePreset[]
}

// Fan grills are square plates matching the fan frame; the hint carries the
// vent opening and mount-hole spacing for lining up guides.
function fan(frame: number, vent: number, mount: number, corner: number): SizePreset {
  return {
    name: `Fan ${frame} mm`,
    hint: `${frame} mm axial fan plate, ${vent} mm vent opening, mount holes at ${mount} mm spacing`,
    canvas: { width: frame, height: frame },
    boundary: { kind: 'rect', width: frame, height: frame, cornerRadius: corner },
  }
}

export const SIZE_GROUPS: SizeGroup[] = [
  {
    group: 'PC fans',
    items: [
      fan(40, 36, 32, 3),
      fan(60, 56, 50, 4),
      fan(80, 75, 71.5, 6),
      fan(92, 86, 82.5, 6),
      fan(120, 113, 105, 7),
      fan(140, 133, 124.5, 7),
      fan(200, 190, 154, 8),
    ],
  },
  {
    group: 'Gaming',
    items: [
      {
        name: 'Steam Machine plate',
        hint: 'Front plate, 156 x 126 mm',
        canvas: { width: 156, height: 126 },
        boundary: { kind: 'rect', width: 156, height: 126, cornerRadius: 2 },
      },
      {
        name: 'Dual 120 vent',
        hint: 'Side or top vent over two 120 mm fans, 240 x 120 mm',
        canvas: { width: 240, height: 120 },
        boundary: { kind: 'rect', width: 240, height: 120, cornerRadius: 6 },
      },
      {
        name: 'Triple 120 vent',
        hint: 'Radiator-style triple 120 mm cutout, 360 x 120 mm',
        canvas: { width: 360, height: 120 },
        boundary: { kind: 'rect', width: 360, height: 120, cornerRadius: 6 },
      },
      {
        name: 'Dual 140 vent',
        hint: 'Vent over two 140 mm fans, 280 x 140 mm',
        canvas: { width: 280, height: 140 },
        boundary: { kind: 'rect', width: 280, height: 140, cornerRadius: 7 },
      },
      {
        name: 'Mini-ITX vent',
        hint: 'Side vent matching a Mini-ITX board, 170 x 170 mm',
        canvas: { width: 170, height: 170 },
        boundary: { kind: 'rect', width: 170, height: 170, cornerRadius: 6 },
      },
    ],
  },
  {
    group: 'DIY',
    items: [
      {
        name: 'Raspberry Pi HAT',
        hint: 'HAT footprint, 65 x 56.5 mm',
        canvas: { width: 65, height: 56.5 },
        boundary: { kind: 'rect', width: 65, height: 56.5, cornerRadius: 3 },
      },
      {
        name: 'UK faceplate',
        hint: 'Single-gang wall plate, 86 x 86 mm',
        canvas: { width: 86, height: 86 },
        boundary: { kind: 'rect', width: 86, height: 86, cornerRadius: 4 },
      },
      {
        name: 'EU faceplate',
        hint: 'Single frame wall plate, 80 x 80 mm',
        canvas: { width: 80, height: 80 },
        boundary: { kind: 'rect', width: 80, height: 80, cornerRadius: 4 },
      },
      {
        name: 'US wall plate',
        hint: 'Single-gang cover, 69.9 x 114.3 mm',
        canvas: { width: 69.9, height: 114.3 },
        boundary: { kind: 'rect', width: 69.9, height: 114.3, cornerRadius: 3 },
      },
      {
        name: 'Drive bay 5.25 in',
        hint: 'Front bay cover, 148.6 x 42 mm',
        canvas: { width: 148.6, height: 42 },
        boundary: { kind: 'rect', width: 148.6, height: 42, cornerRadius: 2 },
      },
      {
        name: 'Speaker 4 in',
        hint: 'Grille for a 4 inch driver, 116 mm circle',
        canvas: { width: 120, height: 120 },
        boundary: { kind: 'circle', radius: 58 },
      },
      {
        name: 'Speaker 6.5 in',
        hint: 'Grille for a 6.5 inch driver, 165 mm circle',
        canvas: { width: 170, height: 170 },
        boundary: { kind: 'circle', radius: 82.5 },
      },
      {
        name: 'Utility 100',
        hint: 'General enclosure vent, 100 x 100 mm',
        canvas: { width: 100, height: 100 },
        boundary: { kind: 'rect', width: 100, height: 100, cornerRadius: 4 },
      },
    ],
  },
  {
    group: 'Pro',
    items: [
      {
        name: 'Rack 1U',
        hint: '19 inch rack panel, 482.6 x 44.45 mm',
        canvas: { width: 482.6, height: 44.45 },
        boundary: { kind: 'rect', width: 482.6, height: 44.45, cornerRadius: 2 },
      },
      {
        name: 'Rack 2U',
        hint: '19 inch rack panel, 482.6 x 88.9 mm',
        canvas: { width: 482.6, height: 88.9 },
        boundary: { kind: 'rect', width: 482.6, height: 88.9, cornerRadius: 2 },
      },
      {
        name: 'Rack 3U',
        hint: '19 inch rack panel, 482.6 x 133.35 mm',
        canvas: { width: 482.6, height: 133.35 },
        boundary: { kind: 'rect', width: 482.6, height: 133.35, cornerRadius: 2 },
      },
      {
        name: 'Eurorack 10HP',
        hint: 'Synth module blank, 50.8 x 128.5 mm',
        canvas: { width: 50.8, height: 128.5 },
        boundary: { kind: 'rect', width: 50.8, height: 128.5, cornerRadius: 2 },
      },
      {
        name: 'Eurorack 20HP',
        hint: 'Synth module blank, 101.6 x 128.5 mm',
        canvas: { width: 101.6, height: 128.5 },
        boundary: { kind: 'rect', width: 101.6, height: 128.5, cornerRadius: 2 },
      },
    ],
  },
  {
    group: 'Beds and sheets',
    items: [
      {
        name: 'Print bed 220',
        hint: 'Fits a 220 x 220 mm printer bed with margin, 210 x 210 mm',
        canvas: { width: 210, height: 210 },
        boundary: { kind: 'rect', width: 210, height: 210, cornerRadius: 4 },
      },
      {
        name: 'Print bed 256',
        hint: 'Fits a 256 x 256 mm printer bed with margin, 246 x 246 mm',
        canvas: { width: 246, height: 246 },
        boundary: { kind: 'rect', width: 246, height: 246, cornerRadius: 4 },
      },
      {
        name: 'Laser sheet A4',
        hint: 'A4 stock, 297 x 210 mm',
        canvas: { width: 297, height: 210 },
        boundary: { kind: 'rect', width: 297, height: 210, cornerRadius: 0 },
      },
      {
        name: 'Laser sheet A3',
        hint: 'A3 stock, 420 x 297 mm',
        canvas: { width: 420, height: 297 },
        boundary: { kind: 'rect', width: 420, height: 297, cornerRadius: 0 },
      },
    ],
  },
]

export interface Recipe {
  name: string
  hint: string
  roll: (seed: number) => DocumentModel
}

const pick = <T,>(rng: Rng, list: T[]): T => list[Math.floor(rng() * list.length)]
const range = (rng: Rng, lo: number, hi: number) => lo + rng() * (hi - lo)

export const RECIPES: Recipe[] = [
  {
    name: 'Turbine Bloom',
    hint: 'Rotational layouts, tangent-locked slots',
    roll: (seed) => {
      const rng = makeRng(seed ^ 0x7ab1)
      return base((d) => {
        d.layout.seed = seed
        d.layout.mode = pick(rng, ['concentric', 'spiral', 'fibonacci'])
        d.shape.kind = pick(rng, ['slot', 'polygon', 'circle'])
        d.shape.slotRatio = range(rng, 0.25, 0.5)
        d.layout.rotateToPath = true
        d.channels.spacing.base = range(rng, 5.5, 9)
        d.channels.size.base = range(rng, 2.5, 4.5)
        d.channels.size.controllers.push(
          ctrl({ a: { x: 60, y: 40 }, value: range(rng, 1, 6), radius: range(rng, 22, 44) }),
        )
      })
    },
  },
  {
    name: 'Crack Basin',
    hint: 'Stone cracks, varied density and rounding',
    roll: (seed) => {
      const rng = makeRng(seed ^ 0x3c2d)
      return base((d) => {
        d.layout.seed = seed
        d.layout.mode = 'voronoi'
        d.layout.voronoi.cornerRadius = range(rng, 0, 2)
        d.channels.spacing.base = range(rng, 7, 13)
        d.channels.size.base = range(rng, 1.4, 3.2)
      })
    },
  },
  {
    name: 'Field Static',
    hint: 'Scatter with one strong attractor',
    roll: (seed) => {
      const rng = makeRng(seed ^ 0x51f3)
      return base((d) => {
        d.layout.seed = seed
        d.layout.mode = 'scatter'
        d.layout.jitter = range(rng, 0, 1)
        d.channels.spacing.base = range(rng, 4, 7)
        d.channels.size.base = range(rng, 1.2, 2.4)
        d.channels.size.controllers.push(
          ctrl({
            a: { x: range(rng, 20, 100), y: range(rng, 15, 65) },
            value: range(rng, 3, 6),
            radius: range(rng, 20, 40),
            falloff: pick(rng, ['smooth', 'sharp', 'soft']),
          }),
        )
      })
    },
  },
  {
    name: 'Loom Shade',
    hint: 'Grids fading along a random axis',
    roll: (seed) => {
      const rng = makeRng(seed ^ 0x9e04)
      return base((d) => {
        d.layout.seed = seed
        d.layout.grid.pattern = pick(rng, ['hex', 'rect'])
        d.channels.spacing.base = range(rng, 4, 6.5)
        d.channels.size.base = range(rng, 2, 3.4)
        const vertical = rng() > 0.5
        d.channels.size.controllers.push(
          ctrl({
            kind: 'line',
            a: vertical ? { x: range(rng, 0, 120), y: 0 } : { x: 0, y: range(rng, 0, 80) },
            b: vertical ? { x: range(rng, 0, 120), y: 80 } : { x: 120, y: range(rng, 0, 80) },
            value: range(rng, 0.6, 1.4),
            radius: range(rng, 30, 60),
            falloff: 'soft',
          }),
        )
      })
    },
  },
]
